import { useCallback, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { Box, Container, Stack, Button, Dialog, DialogTitle, DialogContent, Chip } from '@mui/material';
import { useQuery, useMutation } from '@apollo/client';
import { toast } from 'react-toastify';

import { DataTable, Column } from '../../components/generic/data-table';
import { TableToolbar } from '../../components/generic/table-toolbar';
import { TableRowActions } from '../../components/generic/table-row-actions';
import { GenericSearch } from '../../components/generic/generic.search';
import EditShop from '../../components/generic/edit.shop';
import { DATE_FORMAT } from '../../constants';
import { LIST_ALL_SHOPS, TOGGLE_SHOP_STATUS } from '../../gql/queries';

interface Shop {
	_id: string;
	name: string;
	location: Array<{
		_id: string;
		name: string;
		address: {
			city: string;
		};
	}>;
	createdAt: string;
	updatedAt?: string;
	deletedAt?: string | null;
}

const columns: Column[] = [
	{ title: 'Shop Name', key: 'name' },
	{ 
		title: 'Locations', 
		key: 'location', 
		formatter: (column: Column, data: Shop) => {
			return data.location?.length || 0;
		} 
	},
	{
		title: 'Status',
		key: 'deletedAt',
		formatter: (column: Column, data: Shop) => {
			const isActive = !data.deletedAt;
			return (
				<Chip
					label={isActive ? 'Active' : 'Inactive'}
					color={isActive ? 'success' : 'default'}
					size="small"
				/>
			);
		}
	},
	{ 
		title: 'Created', 
		key: 'createdAt', 
		formatter: (column: Column, data: Shop) => {
			return format(new Date(data.createdAt), DATE_FORMAT);
		} 
	},
];

function ShopList() {
	const navigate = useNavigate();
	const { data, loading, refetch } = useQuery(LIST_ALL_SHOPS, {
		variables: { includeDeleted: true }
	});
	const [toggleShopStatus] = useMutation(TOGGLE_SHOP_STATUS);
	const [showCreateModal, setShowCreateModal] = useState(false);
	const [searchQuery, setSearchQuery] = useState('');

	const handleRowClick = useCallback(
		(shop: Shop) => {
			navigate(`/dashboard/shops/${shop._id}`);
		},
		[navigate]
	);

	const handleEdit = useCallback(
		(shop: Shop) => {
			navigate(`/dashboard/shops/${shop._id}`);
		},
		[navigate]
	);

	const handleToggleStatus = useCallback(
		async (shop: Shop) => {
			const isActive = !shop.deletedAt;
			const action = isActive ? 'deactivate' : 'activate';
			const confirmMessage = isActive
				? `Are you sure you want to deactivate "${shop.name}"? It will be hidden from active operations.`
				: `Are you sure you want to activate "${shop.name}"? It will be available for operations.`;

			if (!window.confirm(confirmMessage)) {
				return;
			}

			try {
				await toggleShopStatus({ variables: { shopId: shop._id } });
				toast.success(`${shop.name} ${action}d successfully.`);
				await refetch();
			} catch (error: any) {
				toast.error(`Failed to ${action} shop: ${error.message}`);
			}
		},
		[toggleShopStatus, refetch]
	);

	const handleSearchChange = useCallback((query: string | null) => {
		setSearchQuery(query || '');
	}, []);

	const handleCloseModal = useCallback(() => {
		setShowCreateModal(false);
		refetch();
	}, [refetch]);

	// Filter shops based on search query
	const filteredShops = useMemo(() => {
		if (!searchQuery || !data?.shops) return data?.shops || [];

		const matchRegExp = new RegExp(searchQuery, 'i');
		return data.shops.filter((shop: Shop) => {
			return (
				matchRegExp.test(shop.name) ||
				shop.location?.some((loc) => matchRegExp.test(loc.name) || matchRegExp.test(loc.address?.city))
			);
		});
	}, [searchQuery, data?.shops]);

	return (
		<Box
			component="main"
			sx={{
				flexGrow: 1,
				py: 8
			}}
		>
			<Container maxWidth="xl">
				<Stack spacing={3}>
					{/* Toolbar with title and create button */}
					<TableToolbar
						title="Shop Management"
						subtitle="Manage all shops in the system. Only Super Admins can create, update, or delete shops."
						actions={
							<Button variant="contained" onClick={() => setShowCreateModal(true)}>
								+ Create Shop
							</Button>
						}
					/>

					{/* Search */}
					<GenericSearch onChange={handleSearchChange} />

					{/* Data Table */}
					<DataTable
						data={filteredShops}
						columns={columns}
						loading={loading}
						onRowClick={handleRowClick}
						renderRowActions={(shop: Shop) => (
							<TableRowActions
								row={shop}
								onEdit={handleEdit}
								customActions={[
									{
										label: shop.deletedAt ? 'Activate' : 'Deactivate',
										onClick: handleToggleStatus,
										color: shop.deletedAt ? 'success.main' : 'warning.main',
									}
								]}
							/>
						)}
					/>
				</Stack>

				{/* Create Shop Modal */}
				<Dialog open={showCreateModal} onClose={handleCloseModal} maxWidth="sm" fullWidth>
					<DialogTitle>Create New Shop</DialogTitle>
					<DialogContent>
						<EditShop onClose={handleCloseModal} refetch={refetch} />
					</DialogContent>
				</Dialog>
			</Container>
		</Box>
	);
}

export default ShopList;
